import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoadingOverlay from '../components/LoadingOverlay';
import PWAInstallModal from '../components/PWAInstallModal';

const API_BASE_URL = 'https://empty-rain-2654.er-hooman.workers.dev';

// SVG Icons
const PhoneIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-500 group-focus-within:text-black transition-colors">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
  </svg>
);

const KeyIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-500 group-focus-within:text-black transition-colors">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z" />
    </svg>
);

const Spinner = ({ color = 'text-black' }) => (
    <svg className={`animate-spin h-6 w-6 ${color}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
);

const SuccessIcon = () => (
    <svg className="h-20 w-20 text-green-600 mx-auto mb-6" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const { login, updateToken } = useAuth();
  
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);

  // Countdown timer for OTP resend
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // Check for PWA install prompt
  useEffect(() => {
    const checkInstallPrompt = () => {
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
      
      if (isMobile && !isStandalone) {
        // Show modal after 2 seconds on login page
        setTimeout(() => {
          setShowInstallModal(true);
        }, 2000);
      }
    };

    checkInstallPrompt();
  }, []);

  // Format phone number for display
  const formatPhoneNumber = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.startsWith('98')) {
      return numbers.slice(2);
    }
    if (numbers.startsWith('0')) {
      return numbers;
    }
    return '0' + numbers;
  };

  // Validate Iranian phone number
  const validatePhoneNumber = (phone) => {
    const cleanPhone = phone.replace(/\D/g, '');
    const iranianMobileRegex = /^(09\d{9}|9\d{9})$/;
    return iranianMobileRegex.test(cleanPhone) || iranianMobileRegex.test('0' + cleanPhone);
  };

  // Send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validatePhoneNumber(phoneNumber)) {
      setError('شماره موبایل وارد شده معتبر نیست. لطفاً شماره موبایل ایرانی وارد کنید.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/firebase/sendOTP`, {
          method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: phoneNumber
        }),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setOtpSent(true);
        setCountdown(120); // 2 minutes countdown
        setError('');
        } else {
        setError(data.details || data.error || 'خطا در ارسال کد تایید');
        }
      } catch (err) {
      console.error('Send OTP error:', err);
      setError('خطا در ارسال کد تایید. لطفاً دوباره تلاش کنید.');
    }
    
    setLoading(false);
  };

  // Verify OTP and login/register
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    
    if (loading) return; // Prevent multiple submissions
    
    if (!otp || otp.length !== 6) {
      setError('کد تایید باید شامل ۶ رقم باشد.');
      return;
    }

    setLoading(true);

    try {
      // First verify OTP
      const verifyResponse = await fetch(`${API_BASE_URL}/api/firebase/verifyOTP`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: phoneNumber,
          otp: otp
        }),
      });

      const verifyData = await verifyResponse.json();
      
      if (!verifyResponse.ok || !verifyData.success) {
        setError(verifyData.details || verifyData.error || 'کد تایید نادرست است');
        setLoading(false);
        return;
      }

      // OTP verified, now login or register
      const loginResponse = await fetch(`${API_BASE_URL}/api/auth/login/phone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: phoneNumber,
          otp: otp
        }),
      });

      const loginData = await loginResponse.json();
      
      if (loginResponse.ok && loginData.idToken) {
        // Update AuthContext with new token
        updateToken(loginData.idToken);
        
        // Track login/registration event
        if (loginData.isNewUser && window.trackRegistration) {
          window.trackRegistration('phone');
        } else if (window.trackLogin) {
          window.trackLogin('phone');
        }
        
        setShowSuccessModal(true);
        setTimeout(() => {
          setShowSuccessModal(false);
          // If new user, go to onboarding, otherwise go to home
          navigate(loginData.isNewUser ? '/onboarding' : '/home', { replace: true });
        }, 2000);
      } else if (loginResponse.status === 409 && loginData.existingUser) {
        // Phone number already exists for another user with email account
        if (loginData.userEmail) {
          setError(`این شماره تلفن به حساب کاربری با ایمیل ${loginData.userEmail} متصل است. لطفاً با ایمیل خود وارد شوید.`);
        } else {
          setError('این شماره تلفن قبلاً برای حساب کاربری دیگری ثبت شده است. لطفاً از طریق ایمیل وارد شوید.');
        }
        
        // Reset form
        setPhoneNumber('');
        setOtp('');
        setOtpSent(false);
        setCountdown(0);
      } else {
        setError(loginData.error || 'خطا در ورود به سیستم');
      }
    } catch (err) {
      console.error('Verify OTP error:', err);
      setError('خطا در تایید کد. لطفاً دوباره تلاش کنید.');
    }
    
    setLoading(false);
  };
  
  // Resend OTP
  const handleResendOtp = async () => {
    if (countdown > 0) return;
    await handleSendOtp({ preventDefault: () => {} });
  };

  // Reset form
  const handleBackToPhone = () => {
    setOtpSent(false);
    setOtp('');
    setError('');
    setCountdown(0);
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 flex flex-col items-center justify-center p-4 font-vazir" dir="rtl">
      <LoadingOverlay 
        isLoading={loading} 
        message={otpSent ? 'در حال تایید کد...' : 'در حال ارسال کد...'}
      />
      
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white p-8 rounded-2xl shadow-xl text-center max-w-md w-full transform transition-all duration-300 ease-out scale-100 opacity-100 ring-1 ring-gray-200">
            <SuccessIcon />
            <h3 className="text-2xl font-bold text-gray-800 mb-3">ورود موفقیت‌آمیز!</h3>
            <p className="text-gray-600 mb-6">خوش آمدید به درمووایز...</p>
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-green-600 animate-progress-bar"></div>
            </div>
          </div>
        </div>
      )}

      <div className="relative w-full max-w-md bg-white text-gray-900 p-8 sm:p-10 rounded-2xl shadow-xl border border-gray-200">
        <div className="text-center mb-10">
          <video
            src="/bot.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="w-24 h-24 mx-auto mb-6 rounded-full shadow-lg object-cover transition-transform duration-300 ease-out hover:scale-110"
            title="Dermowise Bot Animation"
            onError={(e) => { 
              const parent = e.currentTarget.parentElement;
              if (parent) {
                const fallbackImg = document.createElement('img');
                fallbackImg.src = 'https://via.placeholder.com/96?text=🤖';
                fallbackImg.alt = 'Dermowise Bot Placeholder';
                let classes = e.currentTarget.className.replace('object-cover', '').replace(/border-\S+/, '').replace(/border-\d+/, '');
                fallbackImg.className = classes.replace('mx-auto', '').replace('mb-6', '') + ' mx-auto mb-6';
                e.currentTarget.style.display = 'none';
                parent.insertBefore(fallbackImg, e.currentTarget.nextSibling || null); 
              }
            }}
          >
            مرورگر شما از پخش ویدیو پشتیبانی نمی‌کند.
          </video>
          <h1 className="text-4xl font-bold text-gray-800 mb-3 select-none">
            درمووایز
          </h1>
          <p className="text-gray-500 text-lg">
            پلتفرم هوشمند تحلیل و مراقبت از پوست شما
          </p>
        </div>

        <h2 className="font-bold text-center mb-1 text-2xl text-gray-700">
          {otpSent ? 'تایید کد دریافتی' : 'ورود با شماره موبایل'}
            </h2>
            <p className="text-sm text-gray-500 text-center mb-8">
          {otpSent 
            ? `کد تایید به شماره ${phoneNumber} ارسال شد.`
            : 'شماره موبایل خود را وارد کنید تا کد تایید برایتان ارسال شود.'
          }
            </p>

            {error && (
                <div className="bg-red-50 border border-red-300 text-red-800 px-4 py-3 mb-6 rounded-lg shadow-sm flex items-center space-x-3 space-x-reverse" role="alert">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-red-600 flex-shrink-0">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                    </svg>
                  <div className="flex-grow">
                    <p className="font-semibold text-red-900">خطا:</p>
                    <p className="text-sm">{error}</p>
                  </div>
                </div>
            )}

        {!otpSent ? (
          <form onSubmit={handleSendOtp} className="space-y-6">
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                <PhoneIcon />
                </div>
                <input
                id="phoneNumber"
                name="phoneNumber"
                type="tel"
                autoComplete="tel"
                  required
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
                className="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-300 text-gray-900 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all placeholder-gray-500 shadow-sm hover:border-gray-400 focus:bg-gray-50"
                placeholder="۰۹۱۲۳۴۵۶۷۸۹"
                maxLength="11"
                />
              </div>
              
            <div>
                  <button
                type="submit"
                    disabled={loading}
                className="w-full flex items-center justify-center text-lg font-semibold py-3.5 px-6 rounded-xl transition-all duration-300 ease-in-out focus:outline-none focus:ring-4 focus:ring-gray-400 focus:ring-opacity-50 disabled:opacity-60 disabled:cursor-not-allowed shadow-md hover:shadow-lg transform hover:-translate-y-0.5 active:translate-y-0 active:shadow-md bg-black hover:bg-gray-800 text-white"
                  >
                {loading ? <Spinner color="text-white"/> : 'ارسال کد تایید'}
                  </button>
                </div>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className="space-y-6">
                <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                <KeyIcon />
              </div>
                    <input
                id="otp"
                name="otp"
                type="text"
                autoComplete="one-time-code"
                required
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-300 text-gray-900 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all placeholder-gray-500 shadow-sm hover:border-gray-400 focus:bg-gray-50 text-center text-xl tracking-widest"
                placeholder="۱۲۳۴۵۶"
                maxLength="6"
              />
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                className="w-full flex items-center justify-center text-lg font-semibold py-3.5 px-6 rounded-xl transition-all duration-300 ease-in-out focus:outline-none focus:ring-4 focus:ring-gray-400 focus:ring-opacity-50 disabled:opacity-60 disabled:cursor-not-allowed shadow-md hover:shadow-lg transform hover:-translate-y-0.5 active:translate-y-0 active:shadow-md bg-black hover:bg-gray-800 text-white"
                >
                {loading ? <Spinner color="text-white"/> : 'تایید و ورود'}
                </button>
              </div>

            <div className="text-center space-y-3">
              <button
                type="button"
                onClick={handleBackToPhone}
                disabled={loading}
                className="text-sm font-medium text-gray-600 hover:text-black focus:outline-none disabled:opacity-60 transition-colors underline hover:no-underline"
              >
                تغییر شماره موبایل
              </button>
              
              <div className="text-sm">
                {countdown > 0 ? (
                  <span className="text-gray-500">
                    ارسال مجدد کد تا {Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')} دقیقه
                  </span>
                ) : (
                <button
                  type="button"
                    onClick={handleResendOtp}
                  disabled={loading}
                    className="font-medium text-gray-700 hover:text-black focus:outline-none disabled:opacity-60 transition-colors underline hover:no-underline"
                  >
                    ارسال مجدد کد تایید
                </button>
                )}
              </div>
            </div>
          </form>
        )}
      </div>
      
       <p className="mt-12 text-center text-sm text-gray-500 opacity-90">
        &copy; {new Date().getFullYear()} Dermowise. تمامی حقوق محفوظ است.
      </p>

      {/* PWA Install Modal */}
      <PWAInstallModal 
        isOpen={showInstallModal}
        onClose={() => setShowInstallModal(false)}
      />
    </div>
  );
};

export default Login; 